import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';
import { ChangeEvent, useId, useMemo } from 'react';
import { CustomFieldProps } from './types';

import styles from './Form.module.css';
const classNamesCtx = classNames.bind(styles);

interface RadioGroupFieldProps {
	/** Available options */
	options: string[];
	/** */
	defaultOptionSelected?: string;

	value: string | null;
	/** String of the input label */
	labelText: string;
	/** Flag indicating the input is dirty and should apply styles */
	isDirty?: boolean;
	/** Flag indicating the input is in an error state and should apply styles */
	isError?: boolean;
	/** When the input is in an error state, text to display in the tooltip */
	errorText?: string;
	/** Flag indicating that the input is required */
	required?: boolean;

	disabled?: boolean;

	onChange: (event: ChangeEvent<HTMLInputElement>) => void;

	className?: string;
}

/**
 * Wrapper to implement MUI radio group input to provide application-wide default behaviors.
 */
export default function RadioGroupField(props: RadioGroupFieldProps) {
	const uniqueId = useId();

	const radioGroupClasses = classNamesCtx({
		['radio-group']: true,
		dirty: props.isDirty,
		error: props.isError
	});

	const radioClasses = classNamesCtx({
		radio: true,
		dirty: props.isDirty,
		error: props.isError,
		disabled: props.disabled,
		[props.className ?? '']: Boolean(props.className)
	});

	return (
		<FormControl
			className={radioGroupClasses}
			disabled={props.disabled}
		>
			<FormLabel
				id={uniqueId}
				text={props.labelText}
				error={props.isError && !props.disabled}
				errorText={props.errorText}
				required={props.required}
			/>
			<RadioGroup
				aria-labelledby={uniqueId}
				defaultValue={props.defaultOptionSelected}
				value={props.value}
				onChange={props.onChange}
				className={radioClasses}
			>
				{props.options.map((item, index) => (
					<FormControlLabel
						key={`rd-${uniqueId}-${String(index)}`}
						value={item}
						control={
							<Radio
								size='small'
								disabled={props.disabled}
							/>
						}
						label={item}
						disabled={props.disabled}
					/>
				))}
			</RadioGroup>
		</FormControl>
	);
}

export function validateRadioGroupProps(props: CustomFieldProps): props is CustomFieldProps & {
	radioItems: string[];
} {
	return props.radioItems !== undefined;
}

function asRadioGroupInput() {
	return (formikProps: CustomFieldProps) => {
		if (!validateRadioGroupProps(formikProps)) {
			throw new Error('FormikRadioGroupInput did not recieve the required props to render.');
		}

		const { field, form, ...props } = formikProps;

		const { value, initialValue, error, touched } = form.getFieldMeta<string | null>(field.name);

		const { dirty, setFieldValue } = form;

		const isDirty = useMemo(() => {
			if (dirty) {
				return value !== initialValue;
			}
			return false;
		}, [value, initialValue, dirty]);

		return (
			<RadioGroupField
				options={props.radioItems}
				defaultOptionSelected={props.defaultRadioItem}
				value={value}
				onChange={(event) => {
					void setFieldValue(field.name, event.target.value);
				}}
				isError={touched && Boolean(error)}
				labelText={props.label ?? ''}
				isDirty={isDirty}
				errorText={error}
				required={props.required}
				disabled={form.isSubmitting || props.disabled}
				className={props.className}
			/>
		);
	};
}

export const FormikRadioGroupInput = asRadioGroupInput();
